import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, HelpCircle, Pizza, Ruler, Lightbulb, Calculator, Eye, Coins } from 'lucide-react';
import { Question } from '../../../types';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { VisualAidFactory, shouldShowVisualAid } from '../../../visuals';
import { StatusBadge } from './StatusBadge';
import { AnswerComparison } from './AnswerComparison';
import { ExplanationCard } from './ExplanationCard';
import { LatexRenderer } from '../../ui/LatexRenderer';
import { resolveSupportContent } from '../../../support/supportResolver';
import { error as logError } from '../../../utils/logger';
interface FeedbackDisplayProps {
  question: Question;
  wasCorrect: boolean;
  userAnswer: string;
  userAnswerIndex?: number;
  motivationalText: string;
  xpGained: number;
  onNext: () => void;
  isLastQuestion?: boolean;
  mode?: 'full' | 'compact';
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
function normalizeCents(n: unknown): number | null {
  if (!isFiniteNumber(n)) return null;
  return Math.max(0, Math.round(n));
}
function formatMoneyDisplay(cents: number): string {
  const safe = Math.max(0, Math.round(cents));
  const euros = Math.floor(safe / 100);
  const remaining = safe % 100;
  if (remaining === 0) return `€${euros},-`;
  return `€${euros},${remaining.toString().padStart(2, '0')}`;
}
// Slide-up entrance variant for staggered sections
const slideUp = (delay: number) => ({
  initial: {
    opacity: 0,
    y: 30
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    delay,
    duration: 0.45,
    ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
  }
});
/**
 * Unified feedback display component — celebration-first design.
 * Uses StatusBadge for the header, staggered slide-up entrance animations.
 */
export function FeedbackDisplay({
  question,
  wasCorrect,
  userAnswer,
  userAnswerIndex,
  motivationalText,
  xpGained,
  onNext,
  isLastQuestion,
  mode = 'full'
}: FeedbackDisplayProps) {
  // Keyboard listener for Enter key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext]);
  const isMoneyAssembler = question.questionType === 'money';
  const resolvedSupport = useMemo(() => resolveSupportContent(question), [question]);
  const supportVisuals = question.supportAids || [];
  const isVisualInteractive = ['bar_chart', 'line_chart', 'schedule_reading'].includes(question.questionType || '');
  const correctAnswer = useMemo(() => {
    if (isMoneyAssembler) {
      const moneyConfig = question.data?.money as any;
      if (!moneyConfig || typeof moneyConfig.targetCents !== 'number') {
        logError('FeedbackDisplay: Missing targetCents in question.data.money');
        return '€0,00';
      }
      const cents = normalizeCents(moneyConfig.targetCents);
      return cents === null ? '' : formatMoneyDisplay(cents);
    }
    if (isVisualInteractive) {
      const config = question.interactiveConfig || {};
      if (question.questionType === 'bar_chart' || question.questionType === 'line_chart') {
        const data = config.data || [];
        const correctIndex = config.correctIndex;
        const item = data[correctIndex];
        return item?.label || 'Correct antwoord';
      }
      if (question.questionType === 'schedule_reading') {
        const schedule = config.schedule || {
          events: []
        };
        const correctCell = config.correctCell;
        if (correctCell) {
          const event = schedule.events?.find((e: any) => e.row === correctCell.row && e.col === correctCell.col);
          if (event) {
            const colName = schedule.columns?.[correctCell.col] || '';
            const timeName = schedule.timeSlots?.[correctCell.row] || '';
            return `${event.content} (${colName}, ${timeName})`;
          }
        }
        return 'Correct vakje';
      }
      return 'Correct antwoord';
    }
    return question.options[question.correctAnswer];
  }, [isMoneyAssembler, isVisualInteractive, question]);
  const distractorExplanation = !wasCorrect && userAnswerIndex !== undefined ? question.feedback?.distractorExplanations?.[userAnswerIndex.toString()] || 'Helaas, dat was niet het juiste antwoord. Kijk goed naar de uitleg hieronder om te zien hoe het wel moet!' : undefined;
  const showSupportVisuals = supportVisuals.some(aid => shouldShowVisualAid(aid, wasCorrect));
  const getVisualHeader = () => {
    if (isMoneyAssembler) {
      return {
        icon: Coins,
        text: wasCorrect ? 'Jouw bedrag' : 'Vergelijk de bedragen'
      };
    }
    if (!resolvedSupport.visual) return {
      icon: Eye,
      text: 'Kijk maar eens'
    };
    switch (resolvedSupport.visual.type) {
      case 'fraction':
        return {
          icon: Pizza,
          text: 'Zo zie je het voor je'
        };
      case 'geo':
        return {
          icon: Ruler,
          text: 'Even meten/tekenen'
        };
      case 'rule':
        return {
          icon: Lightbulb,
          text: 'Handige regel'
        };
      case 'column':
        return {
          icon: Calculator,
          text: 'Cijferend stap voor stap'
        };
      default:
        return {
          icon: HelpCircle,
          text: 'Kijk maar eens'
        };
    }
  };
  const visualHeader = getVisualHeader();
  const VisualIcon = visualHeader.icon;
  const userMoneyTokens = useMemo(() => {
    if (!isMoneyAssembler) return null;
    try {
      const parsed = JSON.parse(userAnswer);
      if (!Array.isArray(parsed)) {
        logError('Money answer: Expected array, got:', typeof parsed);
        return null;
      }
      if (!parsed.every(token => Number.isFinite(token?.valueCents) && Number.isFinite(token?.count))) {
        logError('Money answer: Invalid token shape:', parsed);
        return null;
      }
      return parsed;
    } catch (error) {
      logError('Money answer parsing failed:', error, 'Raw answer:', userAnswer);
      return null;
    }
  }, [isMoneyAssembler, userAnswer]);
  const correctCents = useMemo(() => {
    if (!isMoneyAssembler) return null;
    return normalizeCents(question.correctAnswer);
  }, [isMoneyAssembler, question.correctAnswer]);
  return <div className="w-full max-w-3xl mx-auto flex flex-col gap-6 pb-10" data-id="element-4312">
      {/* 1. STATUS BADGE — celebration-first header */}
      <motion.div {...slideUp(0)} data-id="element-4313">
        <StatusBadge isCorrect={wasCorrect} motivationalText={motivationalText} xpGained={xpGained} mode={mode} data-id="element-4314" />
      </motion.div>

      {/* 2. QUESTION REMINDER */}
      {mode === 'full' && <motion.div {...slideUp(0.1)} data-id="element-4315">
          <div className="bg-white/70 backdrop-blur-sm p-5 rounded-2xl border border-slate-100 text-center shadow-sm" data-id="element-4316">
            <div className="text-xs font-bold uppercase text-slate-400 mb-2 tracking-wider" data-id="element-4317">
              De vraag was
            </div>
            <div className="text-xl font-bold text-slate-700 leading-snug" data-id="element-4318">
              <LatexRenderer content={question.question} data-id="element-4319" />
            </div>
          </div>
        </motion.div>}

      {/* 3. ANSWER COMPARISON */}
      <motion.div {...slideUp(0.18)} data-id="element-4320">
        <AnswerComparison userAnswer={userAnswer} correctAnswer={correctAnswer} isCorrect={wasCorrect} mode={mode} data-id="element-4321" />
      </motion.div>

      {/* 4. EXPLANATION */}
      {mode === 'full' && <motion.div {...slideUp(0.28)} data-id="element-4322">
          <ExplanationCard explanation={resolvedSupport.explanation || ''} distractorExplanation={distractorExplanation} isCorrect={wasCorrect} mode={mode} solutionSteps={resolvedSupport.solutionSteps} data-id="element-4323" />
        </motion.div>}

      {/* 5. MONEY ASSEMBLER FEEDBACK */}
      {isMoneyAssembler && userMoneyTokens && correctCents !== null && <motion.div {...slideUp(0.32)} data-id="element-4324">
          <Card className="p-8 border-2 border-slate-100 shadow-lg shadow-slate-200/50 bg-white overflow-hidden rounded-[2rem]" data-id="element-4325">
            <div className="flex items-center gap-3 mb-8 text-slate-800 font-black border-b border-slate-100 pb-4" data-id="element-4326">
              <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600" data-id="element-4327">
                <VisualIcon className="w-6 h-6" strokeWidth={2.5} data-id="element-4328" />
              </div>
              <span className="text-xl" data-id="element-4329">{visualHeader.text}</span>
            </div>

            <div className="space-y-8" data-id="element-4330">
              <div data-id="element-4331">
                <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-4332">
                  {wasCorrect ? '✅ Jouw antwoord (correct!)' : '❌ Jouw antwoord'}
                </h4>
                <div className="bg-slate-50 rounded-2xl p-6 border-2 border-dashed border-slate-300" data-id="element-4333">
                  <div className="flex flex-wrap gap-2" data-id="element-4334">
                    {userMoneyTokens.map((token: any, idx: number) => {
                  const getMoneyStyles = (cents: number) => {
                    if (cents >= 500) return 'w-20 h-12 md:w-24 md:h-14 rounded-xl bg-gradient-to-br from-slate-400 via-slate-500 to-slate-600 text-white font-black text-lg shadow-lg border-2 border-slate-700/30';
                    if (cents >= 100) return 'w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-500 text-amber-900 font-black text-base shadow-lg border-2 border-amber-600';
                    if (cents >= 50) return 'w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-yellow-300 via-yellow-100 to-yellow-500 text-yellow-900 font-black text-sm shadow-md border-2 border-yellow-600';
                    if (cents >= 10) return 'w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-yellow-400 via-yellow-200 to-yellow-500 text-yellow-900 font-black text-xs shadow-md border-2 border-yellow-600';
                    return 'w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-orange-400 via-orange-300 to-orange-500 text-orange-900 font-black text-xs shadow-md border-2 border-orange-600';
                  };
                  const getDenominationDisplay = (cents: number): string => {
                    if (cents >= 100) return `€${cents / 100}`;
                    return `${cents}c`;
                  };
                  return <div key={idx} className="flex items-center gap-1" data-id="element-4335">
                          <div className={`flex items-center justify-center ${getMoneyStyles(token.valueCents)}`} data-id="element-4336">
                            <span className="drop-shadow-sm" data-id="element-4337">
                              {getDenominationDisplay(token.valueCents)}
                            </span>
                          </div>
                          {token.count > 1 && <span className="text-sm font-bold text-slate-600 bg-slate-200 px-2 py-1 rounded-full" data-id="element-4338">
                              ×{token.count}
                            </span>}
                        </div>;
                })}
                  </div>
                </div>
              </div>

              {!wasCorrect && <div data-id="element-4339">
                  <h4 className="text-sm font-bold text-emerald-600 uppercase tracking-wider mb-4" data-id="element-4340">
                    ✅ Correct antwoord
                  </h4>
                  <div className="bg-emerald-50 rounded-2xl p-6 border-2 border-emerald-200" data-id="element-4341">
                    <p className="text-center text-2xl font-black text-emerald-700" data-id="element-4342">
                      {formatMoneyDisplay(correctCents)}
                    </p>
                    <p className="text-center text-sm text-emerald-600 mt-2" data-id="element-4343">
                      Er zijn meerdere manieren om dit bedrag te maken!
                    </p>
                  </div>
                </div>}
            </div>
          </Card>
        </motion.div>}

      {/* 6. SUPPORT VISUALS */}
      {!isMoneyAssembler && showSupportVisuals && <motion.div {...slideUp(0.36)} data-id="element-4344">
          <Card className="p-8 border-2 border-slate-100 shadow-lg shadow-slate-200/50 bg-white overflow-hidden rounded-[2rem]" data-id="element-4345">
            <div className="flex items-center gap-3 mb-8 text-slate-800 font-black border-b border-slate-100 pb-4" data-id="element-4346">
              <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600" data-id="element-4347">
                <VisualIcon className="w-6 h-6" strokeWidth={2.5} data-id="element-4348" />
              </div>
              <span className="text-xl" data-id="element-4349">{visualHeader.text}</span>
            </div>
            <div className="space-y-6" data-id="element-4350">
              {supportVisuals.filter(aid => shouldShowVisualAid(aid, wasCorrect)).map((aid, index) => <VisualAidFactory key={index} visualAid={aid} isCorrect={wasCorrect} mode="support" data-id="element-4351" />)}
            </div>
          </Card>
        </motion.div>}

      {/* 7. ACTION BUTTON */}
      {mode === 'full' ? <motion.div {...slideUp(0.42)} className="mt-2 flex justify-center sticky bottom-6 z-20" data-id="element-4352">
          <Button size="xxl" onClick={onNext} variant={wasCorrect ? 'success' : 'primary'} className="w-full md:w-auto md:min-w-[360px] shadow-2xl hover:shadow-2xl transition-all hover:-translate-y-1 group rounded-2xl" data-id="element-4353">
            {wasCorrect ? 'Volgende vraag' : 'Ik snap het, doorgaan'}
            <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" strokeWidth={3} data-id="element-4354" />
          </Button>
        </motion.div> : <div className="flex justify-end pt-4" data-id="element-4355">
          <Button onClick={onNext} variant={wasCorrect ? 'success' : 'primary'} size="lg" data-id="element-4356">
            {isLastQuestion ? 'Klaar' : 'Volgende'}
          </Button>
        </div>}
    </div>;
}